/**
 * Avatar: a person's photo, or their initials over a stable color.
 *
 * This is the ONLY place in the app that renders an avatar. There used to be five,
 * each with its own initials rule (`charAt(0)` with different criteria) and
 * all using the same color for everyone.
 *
 * Decisions that are not obvious from the JSX:
 *
 *   · `onError` falls back to initials. A photo deleted from the bucket (or a row
 *     pointing to an object that no longer exists) should look "no photo", not
 *     like a broken icon.
 *   · `alt=""` by default: on almost every screen the name is written
 *     right next to it, so announcing it again is noise for a screen reader.
 *     When the avatar stands alone, pass an explicit `alt`.
 *   · The color comes from `userId`, not the name — see `avatarPaletteIndex`.
 */
import { type Component, createMemo, createSignal, Show } from "solid-js";
import { avatarPaletteIndex, avatarUrl, cn, initialsOf } from "@time-tracker/shared";
import { SERVER_URL } from "@/lib/runtime-config";

/**
 * Default API origin.
 *
 * The component resolves it by itself so the ~5 places that render an avatar do not
 * have to remember to pass it (and so none picks the wrong origin).
 * `apiBaseUrl` remains a prop for tests and multi-origin scenarios.
 */
const apiBaseUrl = () => SERVER_URL;

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";

/** Diameter and font size by size. */
const SIZE_CLASSES: Record<AvatarSize, string> = {
  xs: "h-6 w-6 text-[10px]",
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-12 w-12 text-base",
  xl: "h-24 w-24 text-2xl",
};

export interface AvatarProps {
  /** Person's ID: the COLOR comes from it (stable even if the name changes). */
  userId?: string | null;
  /** Display name; initials come from it. */
  name?: string | null;
  /** Fallback email when there is no name. */
  email?: string | null;
  /** Storage key (`avatars/…`) or absolute URL. Without this, use initials. */
  image?: string | null;
  /** API origin used to build the URL from the key. */
  apiBaseUrl?: string | null;
  size?: AvatarSize;
  /** Alternative text. Empty = decorative (the name is already beside it). */
  alt?: string;
  class?: string;
  /** Container `data-testid`. */
  testId?: string;
}

export const Avatar: Component<AvatarProps> = (props) => {
  // Store the URL that failed, not a boolean: this way, when the user changes
  // photos, the error state does not remain stuck to the previous image (which
  // is what would happen with an effect-less `failed: boolean` that does not reset it).
  const [failedSrc, setFailedSrc] = createSignal<string | null>(null);

  const size = () => props.size ?? "md";
  const src = createMemo(() => avatarUrl(props.image, props.apiBaseUrl ?? apiBaseUrl()));
  const initials = createMemo(() => initialsOf(props.name, props.email));
  const tone = createMemo(() => avatarPaletteIndex(props.userId));
  const usableSrc = createMemo(() => {
    const url = src();
    return url && url !== failedSrc() ? url : null;
  });

  return (
    <div
      class={cn(
        "relative flex shrink-0 items-center justify-center overflow-hidden rounded-full font-bold select-none",
        SIZE_CLASSES[size()],
        // The tone is applied only when initials are rendered; behind an opaque
        // photo it would be invisible, but it remains so the space has color
        // while the image loads.
        "avatar-tone",
        `avatar-tone-${tone()}`,
        props.class,
      )}
      data-testid={props.testId}
      data-avatar-tone={tone()}
    >
      <Show when={usableSrc()} fallback={<span aria-hidden="true">{initials()}</span>}>
        {(url) => (
          <img
            src={url()}
            alt={props.alt ?? ""}
            class="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
            onError={() => setFailedSrc(url())}
            data-testid={props.testId ? `${props.testId}-image` : undefined}
          />
        )}
      </Show>
    </div>
  );
};
